import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnrollmentService } from '../../services/enrollment.service';
import { LoadingService } from '../../services/loading.service';
import { ProjectService } from '../../services/project.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// Enum para el estado de inscripción (debe coincidir con el backend)
export enum EnrollmentStatus {
  Pending = 0,
  Active = 1,
  Withdrawn = 2,
  Deferred = 3,
  Completed = 4,
  Refinanced = 5,
  Reversed = 6,
  Refund = 7,
  SlotTransfer = 8
}

@Component({
  selector: 'app-enrollments',
  templateUrl: './enrollments.component.html',
  styleUrls: ['./enrollments.component.scss']
})
export class EnrollmentsComponent implements OnInit, OnDestroy {
  individuals: any[] = [];
  corporate: any[] = [];
  view: 'individual' | 'corporate' = 'individual';

  // Debounce
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Filtros
  searchTerm: string = '';
  filterId: number | null = null;
  selectedStatus: string = '';
  selectedProject: any = null;
  inconsistencyFilter: string = '';
  isFixingInconsistencies: boolean = false;

  // Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;

  // Ordenamiento
  sortBy: string = 'createdAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Métricas
  pendingEnrollments: number = 0;
  activeEnrollments: number = 0;
  withdrawnEnrollments: number = 0;
  deferredEnrollments: number = 0;
  activeProjects: number = 0;
  recentEnrollments: number = 0;
  uniqueProjects: any[] = []; // Lista completa para el dropdown

  // Enum disponible para el template
  EnrollmentStatus = EnrollmentStatus;

  // Datos para la tabla
  enrollmentsData: any[] = [];
  filteredData: any[] = [];

  constructor(
    private enrollmentService: EnrollmentService,
    private projectService: ProjectService,
    private loadingService: LoadingService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.route.data.subscribe((data: any) => {
      if (data && data['view']) {
        this.switchView(data['view']);
      } else {
        this.switchView('individual'); // Fallback
      }
    });

    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects() {
    this.projectService.getAll().subscribe((res: any) => {
      if (res && res.data) {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        this.uniqueProjects = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      }
    });
  }

  switchView(view: 'individual' | 'corporate') {
    this.view = view;
    this.pageNumber = 1; // Reset page
    this.searchTerm = '';
    this.filterId = null;
    this.selectedStatus = '';
    this.selectedProject = null;
    this.inconsistencyFilter = '';

    // Recargar datos
    this.loadData();
  }

  onFilterChange() {
    this.filterSubject.next();
  }

  onPageChange(event: any) {
    this.pageNumber = event.page + 1; // PrimeNG es 0-indexed
    this.pageSize = event.rows;
    this.loadData();
  }

  /**
   * Cambia el ordenamiento de la tabla
   * Si se hace clic en la misma columna, alterna la dirección
   * Si es una columna diferente, ordena descendente por defecto
   */
  onSortChange(field: string) {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'desc';
    }
    this.pageNumber = 1;
    this.loadData();
  }

  loadData() {
    if (this.view === 'individual') {
      this.loadIndividuals();
    } else {
      this.loadCorporate();
    }
    this.loadStats();
  }

  private loadIndividuals() {
    const params: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    };

    if (this.searchTerm) params.searchTerm = this.searchTerm;
    if (this.filterId) params.id = this.filterId;
    if (this.selectedStatus) params.status = Number(this.selectedStatus);
    if (this.selectedProject) params.projectId = this.selectedProject;
    if (this.inconsistencyFilter) params.inconsistencyFilter = this.inconsistencyFilter;

    this.loadingService.show();
    this.enrollmentService.getIndividuals(params).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.individuals = res.data.data || [];
          this.totalRecords = res.data.totalCount || 0;
          this.enrollmentsData = this.individuals;
          this.filteredData = this.individuals;
        } else {
          this.individuals = [];
          this.totalRecords = 0;
          this.filteredData = [];
        }
        this.loadingService.hide();
      },
      error: () => {
        this.individuals = [];
        this.totalRecords = 0;
        this.filteredData = [];
        this.loadingService.hide();
      }
    });
  }

  private loadCorporate() {
    const params: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    };

    if (this.searchTerm) params.searchTerm = this.searchTerm;
    if (this.filterId) params.id = this.filterId;
    if (this.selectedStatus) params.status = Number(this.selectedStatus);
    if (this.selectedProject) params.projectId = this.selectedProject;
    if (this.inconsistencyFilter) params.inconsistencyFilter = this.inconsistencyFilter;

    this.loadingService.show();
    this.enrollmentService.getCorporate(params).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.corporate = res.data.data || [];
          this.totalRecords = res.data.totalCount || 0;
          this.enrollmentsData = this.corporate;
          this.filteredData = this.corporate;
        } else {
          this.corporate = [];
          this.totalRecords = 0;
          this.filteredData = [];
        }
        this.loadingService.hide();
      },
      error: () => {
        this.corporate = [];
        this.totalRecords = 0;
        this.loadingService.hide();
      }
    });
  }

  private loadStats() {
    const type = this.view === 'individual' ? 'individual' : 'corporate';
    this.enrollmentService.getStats(type).subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          this.pendingEnrollments = res.data.pending || 0;
          this.activeEnrollments = res.data.active || 0;
          this.withdrawnEnrollments = res.data.withdrawn || 0;
          this.deferredEnrollments = res.data.deferred || 0;
          this.activeProjects = res.data.uniqueProjects || 0;
          this.recentEnrollments = res.data.recent || 0;
        }
      }
    });
  }

  getInitials(firstName: string, lastName: string): string {
    return `${(firstName || '')[0]}${(lastName || '')[0]}`.toUpperCase();
  }

  getCompanyInitials(companyName: string): string {
    if (!companyName) return 'CO';
    const words = companyName.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return companyName.substring(0, 2).toUpperCase();
  }

  // ============== MÉTODOS PARA ESTADO DE INSCRIPCIÓN ==============

  getEnrollmentStatusLabel(status: number | string): string {
    const statusStr = String(status).toLowerCase();
    const statusMap: { [key: string]: string } = {
      '0': 'Pendiente',
      '1': 'Activa',
      '2': 'Retirada',
      '3': 'Aplazada',
      '4': 'Completada',
      '5': 'Refinanciada',
      '6': 'Reversada',
      '7': 'Reintegro',
      'pending': 'Pendiente',
      'active': 'Activa',
      'withdrawn': 'Retirada',
      'deferred': 'Aplazada',
      'completed': 'Completada',
      'refinanced': 'Refinanciada',
      'reversed': 'Reversada',
      'refund': 'Reintegro',
      'overdue': 'Vencida',
      '8': 'Cesión de Cupo',
      'slottransfer': 'Cesión de Cupo'
    };
    return statusMap[statusStr] || 'Desconocido';
  }

  getEnrollmentStatusClass(status: number | string): string {
    const statusStr = String(status).toLowerCase();
    if (statusStr === '0' || statusStr === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (statusStr === '1' || statusStr === 'active') return 'bg-green-100 text-green-800';
    if (statusStr === '2' || statusStr === 'withdrawn') return 'bg-red-100 text-red-800';
    if (statusStr === '3' || statusStr === 'deferred') return 'bg-orange-100 text-orange-800';
    if (statusStr === '4' || statusStr === 'completed') return 'bg-blue-100 text-blue-800';
    if (statusStr === '5' || statusStr === 'refinanced') return 'bg-indigo-100 text-indigo-800';
    if (statusStr === '6' || statusStr === 'reversed') return 'bg-gray-100 text-gray-800';
    if (statusStr === '7' || statusStr === 'refund') return 'bg-purple-100 text-purple-800';
    if (statusStr === '8' || statusStr === 'slottransfer') return 'bg-teal-100 text-teal-800';
    if (statusStr === 'overdue') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  onInconsistencyFilterChange() {
    this.pageNumber = 1;
    this.loadData();
  }

  fixInconsistencies() {
    if (this.isFixingInconsistencies) return;
    this.isFixingInconsistencies = true;
    this.enrollmentService.fixInconsistencies().subscribe({
      next: (res: any) => {
        this.isFixingInconsistencies = false;
        if (res.isSuccess) {
          const data = res.data;
          alert(`Inconsistencias corregidas: ${data.totalFixed} cuotas (${data.individualPlansFixed} individuales, ${data.corporatePlansFixed} corporativas)`);
          this.loadData();
        } else {
          alert('Error al corregir inconsistencias: ' + (res.errorMessage || 'Error desconocido'));
        }
      },
      error: (err: any) => {
        this.isFixingInconsistencies = false;
        alert('Error al corregir inconsistencias: ' + (err.message || 'Error desconocido'));
      }
    });
  }

  isWithdrawnOrDeferred(status: number | string): boolean {
    const statusStr = String(status);
    return statusStr === '2' || statusStr === '3' ||
      statusStr === 'Withdrawn' || statusStr === 'Deferred';
  }

  getEnrollmentStatusIcon(status: number | string): string {
    const statusStr = String(status);
    if (statusStr === '0' || statusStr === 'Pending') return '⏳';
    if (statusStr === '1' || statusStr === 'Active') return '✓';
    if (statusStr === '2' || statusStr === 'Withdrawn') return '⚠';
    if (statusStr === '3' || statusStr === 'Deferred') return '⏸';
    if (statusStr === '4' || statusStr === 'Completed') return '✔';
    if (statusStr === '5' || statusStr === 'Refinanced') return '🔄';
    if (statusStr === '6' || statusStr === 'Reversed') return '↩️';
    if (statusStr === '7' || statusStr === 'Refund') return '💸';
    return '?';
  }

  /** Tono cromático determinístico para el avatar de iniciales (a-f). */
  getInitialsTone(seed: string | null | undefined): string {
    const s = (seed || '?').toUpperCase();
    const code = s.charCodeAt(0) || 0;
    const tones = ['a', 'b', 'c', 'd', 'e', 'f'];
    return tones[code % tones.length];
  }
}
